import * as React from 'react';

import { Info20Regular } from '@fluentui/react-icons';

import { cn } from '@avodah-inc/ui/utils';

import { Button } from '../../atoms/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../atoms/tooltip';
import { Typography } from '../../atoms/typography';
import { UnstyledButton } from '../../atoms/unstyled-button';
import { Card } from '../card';
import { ColorChip } from './color-chip';
import { useContextHighlighter } from './hooks/use-context-text-highlighter';

export type SymptomCardProps = React.HTMLAttributes<HTMLDivElement> & {
  mainSessionRef?: React.RefObject<HTMLDivElement>;
  chip: {
    label: string;
    count: number;
    className?: string;
  };
  icd10Code?: string;
  icd10Description?: string;
  hitContexts?: {
    symptom: string;
    synonyms: string[];
    sentence: string;
    startWord: number;
    color: string;
    workflowStep: string;
  }[];
  onPress?: () => void;
};

export const SymptomCard = React.forwardRef<HTMLDivElement, SymptomCardProps>(
  ({ ...props }, ref) => {
    const [expanded, setExpanded] = React.useState(false);
    const { handleContextMapping } = useContextHighlighter();

    const highlightText = React.useCallback(
      (text: string, color: string) => {
        if (!props.hitContexts) return text;
        return text
          .replace(/^[^a-zA-Z]+|[^a-zA-Z]+$/g, '')
          .split(/\b/)
          .map((part: string, index) => {
            const isHighlighted = props.hitContexts?.some((context) =>
              new RegExp(
                `\\b(${[context.symptom, ...context.synonyms].join('|')})\\b`,
                'gi',
              ).test(part),
            );
            return isHighlighted ? (
              <span className={`!px-0 text-${color}`} key={`${part}-${index}`}>
                {part}
              </span>
            ) : (
              part
            );
          });
      },
      [props.hitContexts],
    );
    return (
      <Card
        ref={ref}
        className={cn(
          'z-50 flex h-fit w-full flex-col gap-y-3 overflow-clip rounded-lg border-gray-300 p-4 outline-none',
          props.className,
        )}
      >
        <header>
          <div className="flex w-full items-center justify-between gap-2">
            <ColorChip
              variant="sm"
              text={props.chip.label}
              rightSideContent={props.chip.count}
              className={cn(props.chip.className, 'capitalize', 'truncate')}
            />
            <div className="flex h-fit w-fit min-w-fit flex-row items-center justify-end gap-x-1 text-center text-gray-400">
              <Typography
                variant="body4"
                className="mt-[0.188rem] text-gray-400"
              >
                {props.icd10Code}
              </Typography>
              <Tooltip>
                <TooltipTrigger className="flex h-fit items-center justify-center">
                  <Info20Regular />
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                  {props.icd10Description}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </header>
        <section className="flex flex-col items-start gap-y-3">
          <div className="flex w-full flex-col gap-y-[0.188rem]">
            {props.hitContexts ? (
              (expanded
                ? props.hitContexts
                : props.hitContexts.slice(0, 5)
              ).map((context) => (
                <ul
                  key={`${context.symptom}-${context.startWord}`}
                  data-state={expanded}
                  className="list-none space-y-2 pl-4 leading-5"
                >
                  <li className="flex">
                    <Typography className="mr-2 " variant="body4">
                      •
                    </Typography>
                    <UnstyledButton
                      className="w-full min-w-0 justify-start !p-0"
                      onPress={() => {
                        props.onPress?.();
                        handleContextMapping({
                          mainElement: props.mainSessionRef?.current || null,
                          text: context.sentence,
                          color: context.color,
                          synonyms: context.synonyms,
                          workflowStep: context.workflowStep,
                        });
                      }}
                    >
                      <Typography className="whitespace-wrap" variant="body4">
                        {highlightText(context.sentence, context.color)}
                      </Typography>
                    </UnstyledButton>
                  </li>
                </ul>
              ))
            ) : (
              <Typography className="mr-2" variant="body4">
                No details available.
              </Typography>
            )}
          </div>
          {props.hitContexts && props.hitContexts.length > 5 && (
            <Button
              variant="text"
              onPress={() => {
                setExpanded((prev) => !prev);
              }}
            >
              <Typography variant="body4" className="text-primary">
                {!expanded ? 'Show More' : 'Show Less'}
              </Typography>
            </Button>
          )}
        </section>
      </Card>
    );
  },
);

SymptomCard.displayName = 'SymptomCard';
